import React, { useContext, useEffect, useRef } from "react";
import { useState } from "react";
import { Animated, Dimensions, Image, Linking, Platform, Pressable, ScrollView, TouchableOpacity } from "react-native";
import { StyleSheet, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import uuid from 'react-native-uuid';

import Icon from 'react-native-vector-icons/Ionicons';
import { PlaceColors } from "../helpers/PlaceColors";
import { useTranslation } from "react-i18next";
import { FindifyBasicModal } from "./FindifyBasicModal";
import { FindifyDataContext, FindifyDataContextType } from "@/contexts/FindifyDataContext";
import { PlaceText } from "./PlaceText";
import { getTranslatedString } from "../helpers/FindifyTranslatedString";
import Svg, { Path } from 'react-native-svg';
import { getFileUrl, IS_QA_MODE, setQAMODE } from "../../../config/environment";
import { DefaultShadow } from "../helpers/DefaultShadows";
import { openSocial } from "../PlaceInfo";
import { FindifyMenuContext, FindifyMenuContextType } from "@/contexts/FindifyMenuContext";
import { FindifyToast } from "./FindifyToast";
import Config from "react-native-config";
import { getApiFromModule } from "../helpers/GetContentsByModule";
import { getFeaturedImageUrl } from "../helpers/DetailsHelpers";

export const navigateToBottomBar = async ({
    navigation,
    path,
    params,
    replace,
}: {
    navigation: any,
    path: String,
    params?: any,
    replace?: boolean
}
) => {
    if (navigation.canGoBack()) {
        await navigation.popToTop();
    }
    if (replace) {
        await navigation.replace(path, params ?? {});
    } else {
        await navigation.push(path, params ?? {});
    }
}

export let BottomNavigationPadding = 0;

const updateBottomNavigationPadding = (findifyDataContext: FindifyDataContextType) => {
    if (findifyDataContext.visitWorkspaceSelected && findifyDataContext?.workspace?.app_settings?.enable_sidemenu) {
        BottomNavigationPadding = Platform.OS === 'ios' ? 10 : 20;
    } else {
        BottomNavigationPadding = Platform.OS === 'ios' ? 52 : 100;
    }
}

const resolveInternalMenuUrl = (url: any, visitVersion: FindifyDataContextType['visitVersion']) => {
    const rawUrl = String(url ?? '').trim().replace(/^\/+/, '').split(/[?#]/, 1)[0];
    const [rawModule, ...slugParts] = rawUrl.split('/');
    const moduleName = rawModule?.toLocaleLowerCase();
    const module = moduleName === 'poi' || moduleName === 'pois' || moduleName === 'points_of_interest'
        ? 'points_of_interest'
        : moduleName === 'content' || moduleName === 'contents'
            ? 'contents'
            : null;

    if (!module || slugParts.length === 0 || slugParts.some((part) => !part)) return null;

    const normalSlug = slugParts.join('/');
    return {
        module,
        // Backoffice stores the standard slug only. Accessible versions follow
        // the convention `<standard-slug>-acessivel`.
        page_slug: visitVersion === 'accessible' && !normalSlug.toLocaleLowerCase().endsWith('-acessivel')
            ? `${normalSlug}-acessivel`
            : normalSlug,
    };
};

const openMenuItem = (item: any, navigation: any, t: any, findifyDataContext: FindifyDataContextType, isLogged: boolean) => {
    const openExternalUrl = (url: string) => {
        if (Platform.OS === "web" && typeof window !== "undefined") {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }
        Linking.openURL(url);
    }

    switch (item.type) {
        case 'change_current_workspace':
            findifyDataContext?.setVisitWorkspaceSelected(false)
            break;
        case 'profile':
            navigateToBottomBar({ navigation, path: 'Profile' });
            break;
        case 'url':
            const internalDestination = resolveInternalMenuUrl(item.url, findifyDataContext?.visitVersion);
            if (internalDestination) {
                navigateToBottomBar({
                    navigation,
                    path: 'PlaceContentDetails',
                    params: internalDestination,
                });
            } else if (item.url && item.url.toLowerCase().includes('tictactoe')) {
                navigateToBottomBar({ navigation, path: 'TicTacToePage' });
            } else if (item.url && (item.url.toLowerCase().includes('memorygame') || item.url.toLowerCase().includes('memory-game'))) {
                navigateToBottomBar({ navigation, path: 'MemoryGamePage' });
            } else if (item.url && item.url.toLowerCase().includes('plants')) {
                navigateToBottomBar({ navigation, path: 'PlantsPage' });
            } else if (item.url && (item.url.toLowerCase().startsWith('https://') || item.url.toLowerCase().startsWith('http://'))) {
                openExternalUrl(item.url);
            } else {
                navigateToBottomBar({ navigation, path: 'WebViewPage', params: { url: item.url, title: item.label } });
            }
            break;
        case 'module':
            switch (item.module) {
                case 'info':
                    navigateToBottomBar({ navigation, path: 'PlaceInfo' });
                    break;
                case 'map':
                    let props: any = {};
                    if (item.map_category) {
                        props['map_category'] = item.map_category;
                    }
                    navigateToBottomBar({ navigation, path: 'PlaceMap', params: props });
                    break;
                case 'scan_qr':
                    navigateToBottomBar({ navigation, path: 'Scan' });
                    break;
                case 'store':
                    navigateToBottomBar({ navigation, path: 'Store' });
                    break;
                case 'start':
                    navigateToBottomBar({ navigation, path: 'Place' });
                    break;
                case 'schedule':
                    navigateToBottomBar({ navigation, path: 'SchedulePage' });
                    break;
                case 'tickets':
                    navigateToBottomBar({ navigation, path: 'TicketsListZonePage' });
                    break;
                case 'checkout':
                    if (!isLogged) {
                        FindifyToast({
                            id: uuid.v4(),
                            visibilityTime: 1500,
                            type: 'error',
                            message: t('checkout.error_user_need_to_login'),
                        })
                        return false;
                    } else {
                        navigateToBottomBar({ navigation, path: 'CheckoutPage' });
                    }
                    break;
            }
            break;
        case 'content':
            navigateToBottomBar({
                navigation, path: 'PlaceContentDetails', params: {
                    module: 'contents',
                    content_id: item.content_id,
                    page_slug: item.page_slug,
                }
            });
            break;
        case 'point_of_interest':
            navigateToBottomBar({
                navigation, path: 'PlaceContentDetails', params: {
                    module: 'points_of_interest',
                    content_id: item.point_of_interest_id,
                    page_slug: item.page_slug,
                }
            });
            break;
        case 'contents':
            navigateToBottomBar({
                navigation,
                path: 'PlaceContentList',
                params: { module: item.content_type, category: item.content_category }
            })
            break;
        default: break;
    }
    return true
}

const makeModalMenuItem = (setModalOpen: any, item: any, index: any, navigation: any, t: any, findifyDataContext: any, isLogged: boolean) => {
    return <Pressable key={'modal_menu_item_' + index} onPressIn={() => {
        let success = openMenuItem(item, navigation, t, findifyDataContext, isLogged);
        if (success) {
            setModalOpen(false);
        }
    }}>
        <View style={modalStyle.item}>
            <Icon name={item?.icon} size={30} color={PlaceColors(findifyDataContext).bottomBarIcon.get()} />
            <PlaceText style={{ color: PlaceColors(findifyDataContext).bottomBarIcon.get() }}>{getTranslatedString(item.label, findifyDataContext)}</PlaceText>
        </View>
    </Pressable>
}

const makeBottomTabIcon = (path: string, icon: string, navigation: any, currentPage: string, findifyDataContext: any, isInitialPage = false, replace = true) => {
    return <Icon
        onPress={() => navigateToBottomBar({ navigation, path, replace })}
        name={icon}
        size={30}
        color={
            isInitialPage ? PlaceColors(findifyDataContext).background.get() :
                currentPage.startsWith(path) ?
                    PlaceColors(findifyDataContext).bottomBarIconSelected.get() :
                    PlaceColors(findifyDataContext).bottomBarIcon.get()} />
}

const makeBiscainhosBottomTabIcon = ({
    icon,
    label,
    active,
    onPress,
    findifyDataContext,
}: {
    icon: string,
    label: string,
    active: boolean,
    onPress: Function,
    findifyDataContext: FindifyDataContextType,
}) => {
    const colors = PlaceColors(findifyDataContext);
    const iconColor = active ? colors.bottomBarIconSelected.get() : colors.bottomBarIcon.get();
    const textColor = active ? colors.bottomBarIconSelected.get() : colors.bottomBarIcon.get();

    return <Pressable
        onPress={() => onPress()}
        style={[
            styles.biscainhosBottomItem,
            active && [
                styles.biscainhosBottomItemActive,
                { backgroundColor: colors.secondary.withAlpha(55) },
            ],
        ]}
    >
        <Icon name={icon} size={22} color={iconColor} />
        <PlaceText style={[styles.biscainhosBottomLabel, { color: textColor }]}>
            {label}
        </PlaceText>
    </Pressable>
}

const getFooterLogoImageUrl = (content: any) => {
    const featuredImage = getFeaturedImageUrl(content);
    if (featuredImage) return featuredImage;

    const galleryImage = content?.galleries?.[0]?.media?.file_path
        || content?.galleries?.[0]?.file?.file_path
        || content?.galleries?.[0]?.file_path
        || content?.gallery?.[0]?.media?.file_path
        || content?.gallery?.[0]?.file_path;

    return galleryImage ? `${getFileUrl()}${galleryImage}` : undefined;
}

export const BiscainhosFooterLogo = ({
    findifyDataContext,
    variant = 'bottom',
}: {
    findifyDataContext: FindifyDataContextType,
    variant?: 'bottom' | 'sidemenu',
}) => {
    const [imageUrl, setImageUrl] = useState<string | undefined>();
    const [secondaryImageUrl, setSecondaryImageUrl] = useState<string | undefined>();

    useEffect(() => {
        let isMounted = true;
        if (Number(findifyDataContext?.workspace?.workspace_id) !== 155 || !findifyDataContext?.workspace?.primary_domain?.domain_name) {
            setImageUrl(undefined);
            setSecondaryImageUrl(undefined);
            return;
        }

        const api = getApiFromModule('contents', findifyDataContext);
        api?.get('logo-footer', null)
            .then((content: any) => {
                if (!isMounted) return;
                setImageUrl(getFooterLogoImageUrl(content));
            })
            .catch(() => {
                if (isMounted) setImageUrl(undefined);
            });

        // The second institutional logo is maintained as content 835.
        api?.get(835, null)
            .then((content: any) => {
                if (!isMounted) return;
                setSecondaryImageUrl(getFooterLogoImageUrl(content));
            })
            .catch(() => {
                if (isMounted) setSecondaryImageUrl(undefined);
            });

        return () => {
            isMounted = false;
        }
    }, [findifyDataContext?.workspace?.workspace_id, findifyDataContext?.workspace?.primary_domain?.domain_name]);

    if (!imageUrl && !secondaryImageUrl) return null;

    return <View style={[
        styles.biscainhosFooterLogoWrap,
        variant === 'sidemenu' ? styles.biscainhosFooterLogoWrapSidemenu : styles.biscainhosFooterLogoWrapBottom,
    ]}>
        {imageUrl ? <Image
            source={{ uri: imageUrl }}
            resizeMode="contain"
            style={[
                styles.biscainhosFooterLogo,
                variant === 'sidemenu' ? styles.biscainhosFooterLogoSidemenu : null,
            ]}
        /> : null}
        {secondaryImageUrl ? <Image
            source={{ uri: secondaryImageUrl }}
            resizeMode="contain"
            style={[
                styles.biscainhosFooterLogo,
                styles.biscainhosFooterLogoSecondary,
                variant === 'sidemenu' ? styles.biscainhosFooterLogoSidemenu : null,
            ]}
        /> : null}
    </View>
}

const isBiscainhosEnglish = (language?: string) => language?.startsWith('en');
const biscainhosText = (findifyDataContext: FindifyDataContextType, pt: string, en: string) => (
    isBiscainhosEnglish(findifyDataContext?.language) ? en : pt
);

const getBiscainhosMenuPath = (item: any) => {
    if (item?.type === 'url' && item?.url?.toLowerCase?.().includes('plants')) return 'PlantsPage';
    if (item?.type === 'profile') return 'Profile';
    if (item?.type === 'content') return 'PlaceContentDetails';
    if (item?.type === 'contents') return 'PlaceContentList';
    if (item?.type === 'module') {
        switch (item.module) {
            case 'info': return 'PlaceInfo';
            case 'map': return 'PlaceMap';
            case 'scan_qr': return 'Scan';
            case 'store': return 'Store';
            case 'start': return 'Place';
            case 'schedule': return 'SchedulePage';
            case 'tickets': return 'TicketsListZonePage';
            case 'checkout': return 'CheckoutPage';
            default: return '';
        }
    }
    return '';
}

const isBiscainhosMenuItemActive = (item: any, currentPage: string, currentParams: any) => {
    const path = getBiscainhosMenuPath(item);
    if (!path) return false;
    if (path === 'PlantsPage') return currentPage === 'PlantsPage' || currentPage === 'PlaceMap';
    if (path === 'PlaceContentDetails') {
        return currentPage === 'PlaceContentDetails'
            && currentParams?.module === 'contents'
            && Number(currentParams?.content_id) === Number(item?.content_id);
    }
    if (path === 'PlaceContentList') {
        return currentPage === 'PlaceContentList'
            && (!item?.content_type || currentParams?.module === item.content_type)
            && (!item?.content_category || Number(currentParams?.category) === Number(item.content_category));
    }
    return currentPage === path;
}

export const BiscainhosBottomNavigation = ({
    navigation,
    findifyDataContext,
    currentPage,
    currentParams,
    availableMenuItems,
    isLogged,
}: {
    navigation: any,
    findifyDataContext: FindifyDataContextType,
    currentPage: string,
    currentParams: any,
    availableMenuItems: any[],
    isLogged: boolean,
}) => {
    const insets = useSafeAreaInsets();
    const { t } = useTranslation();
    const defaultBottomItems = [
        { type: 'module', module: 'start', icon: 'home-outline', label: { 'pt-PT': 'Início', 'en-US': 'Home', 'en-GB': 'Home', 'fr-FR': 'Accueil' } },
        { type: 'url', url: 'plants', icon: 'map-outline', label: { 'pt-PT': 'Mapa', 'en-US': 'Map', 'en-GB': 'Map', 'fr-FR': 'Plan' } },
        { type: 'contents', content_type: 'points_of_interest', icon: 'volume-high-outline', label: { 'pt-PT': 'Áudio', 'en-US': 'Audio', 'en-GB': 'Audio', 'fr-FR': 'Audio' } },
        { type: 'content', content_id: 789, icon: 'happy-outline', label: { 'pt-PT': 'Avaliar', 'en-US': 'Review', 'en-GB': 'Review', 'fr-FR': 'Évaluer' } },
    ];
    const menuItems = (availableMenuItems?.length ? availableMenuItems : defaultBottomItems).slice(0, 5);

    return <View style={[
            styles.biscainhosBottomContainer,
            {
                position: Platform.OS === 'web' ? 'fixed' as any : 'absolute',
                paddingBottom: Math.max(insets.bottom, 8),
                backgroundColor: PlaceColors(findifyDataContext).bottomBar.get(),
                borderTopColor: PlaceColors(findifyDataContext).secondary.withAlpha(90),
            },
        ]}>
            {menuItems.map((item: any, index: number) => <React.Fragment key={`biscainhos-bottom-${index}`}>
                {makeBiscainhosBottomTabIcon({
                    icon: item?.icon || defaultBottomItems[index]?.icon || 'ellipse-outline',
                    label: getTranslatedString(item?.label, findifyDataContext) || defaultBottomItems[index]?.label?.['pt-PT'] || '',
                    active: isBiscainhosMenuItemActive(item, currentPage, currentParams),
                    onPress: () => openMenuItem(item, navigation, t, findifyDataContext, isLogged),
                findifyDataContext,
            })}
        </React.Fragment>)}
    </View>
}

const groupMenuItems = (items: any[]) => {
    const divider = items.length > 9 ? 4 : 3;
    const rows: any[] = [];
    for (let i = 0; i < items.length; i += divider) {
        rows.push(items.slice(i, i + divider));
    }
    return rows;
}

const getMenuTitleValues = (menu: any) => {
    const value = menu?.title ?? menu?.name ?? menu?.label;
    if (!value) return [];
    if (typeof value === 'string') return [value];
    if (typeof value === 'object') return Object.values(value).filter(Boolean).map((line: any) => String(line));
    return [];
}

const normalizeMenuItem = (item: any) => {
    const source = {
        ...item,
        ...(item?.menu_item ?? {}),
        ...(item?.item ?? {}),
    };

    const declaredType = String(source.type ?? '').trim().toLocaleLowerCase();
    // Some menu payloads already carry a `type`, but retain the backoffice
    // `item_id` field. Normalize it before routing so a content detail always
    // receives the `content_id` expected by PlaceContentDetails.
    if (declaredType === 'content' && !source.content_id && source.item_id) {
        return {
            ...source,
            type: 'content',
            content_id: source.item_id,
            page_slug: source.page_slug ?? source.item_link,
        };
    }
    if ((declaredType === 'point_of_interest' || declaredType === 'poi') && !source.point_of_interest_id && source.item_id) {
        return {
            ...source,
            type: 'point_of_interest',
            point_of_interest_id: source.item_id,
            page_slug: source.page_slug ?? source.item_link,
        };
    }
    if (source.type || source.module || source.url || source.content_id) return source;

    const label = source.item_name ?? source.label ?? source.title ?? source.name;
    const itemType = String(source.item_type ?? '').trim().toLocaleLowerCase();
    const itemId = source.item_id;
    const itemLink = source.item_link;

    switch (itemType) {
        case 'contents':
        case 'content':
            return {
                ...source,
                type: 'content',
                label,
                content_id: itemId,
                page_slug: itemLink,
                icon: source.icon ?? 'document-text-outline',
            };
        case 'points_of_interest':
        case 'point_of_interest':
        case 'poi':
            return {
                ...source,
                type: 'point_of_interest',
                label,
                point_of_interest_id: itemId,
                page_slug: itemLink,
                icon: source.icon ?? 'location-outline',
            };
        case 'contents_categories':
        case 'content_categories':
            return {
                ...source,
                type: 'contents',
                label,
                content_type: 'contents',
                content_category: itemId,
                icon: source.icon ?? 'albums-outline',
            };
        case 'points_of_interest_categories':
        case 'point_of_interest_categories':
        case 'pois_categories':
            return {
                ...source,
                type: 'contents',
                label,
                content_type: 'points_of_interest',
                content_category: itemId,
                icon: source.icon ?? 'map-outline',
            };
        case 'events_categories':
        case 'event_categories':
            return {
                ...source,
                type: 'contents',
                label,
                content_type: 'events',
                content_category: itemId,
                icon: source.icon ?? 'calendar-outline',
            };
        case 'store':
            return {
                ...source,
                type: 'module',
                module: 'store',
                label,
                icon: source.icon ?? 'bag-outline',
            };
        case 'external':
        case 'url':
        case 'link':
            return {
                ...source,
                type: 'url',
                url: itemLink,
                label,
                icon: source.icon ?? 'open-outline',
            };
        default:
            return {
                ...source,
                type: itemLink ? 'url' : source.type,
                url: itemLink,
                label,
                icon: source.icon ?? 'ellipse-outline',
            };
    }
};

export const FindifyNavigation = ({ navigation }: any) => {
    const findifyMenuContext = useContext(FindifyMenuContext)

    const [availableModules, setAvailableModules] = useState<any>([]);
    const [sideMenuModules, setSideMenuModules] = useState<any>([]);
    const [sideMenuItems, setSideMenuItems] = useState<any[]>([]);
    const [availableMenuItems, setAvailableMenuItems] = useState<any[]>([]);
    const { t, i18n } = useTranslation();
    const findifyDataContext = useContext(FindifyDataContext);
    const navState = navigation.getState();
    const insets = useSafeAreaInsets();
    // const [mode, setMode] = useState<'bottomNav' | 'sideMenu'>('bottomNav');
    // const currentPage = navState.routeNames[navState.index];
    const currentRoute = navState.routes[navState.index];
    const currentPage = currentRoute.name;
    const currentParams = currentRoute.params ?? {};

    const isInitialPage = !findifyDataContext.visitWorkspaceSelected;

    const [QAPressTimer, setQAPressTimer] = useState(null);
    const [QAPressCount, setQAPressCount] = useState(0);

    const [isLogged, setIsLogged] = useState(false);

    useEffect(() => {
        if (findifyDataContext?.userCustomerAccounts[findifyDataContext?.workspace?.workspace_id]) {
            setIsLogged(true);
        } else {
            setIsLogged(false);
        }
    }, [findifyDataContext?.userCustomerAccounts]);

    // QA MODE
    useEffect(() => {
        if (QAPressCount === 15) {
            // Execute your function when the condition is met
            setQAMODE(!IS_QA_MODE);
            if (navigation.canGoBack()) {
                navigation.popToTop();
            }
            navigation.replace('IntialPage', { IS_QA_MODE });
            // Reset the press count
            setQAPressCount(0);
        }
    }, [QAPressCount]);


    useEffect(() => {
        updateBottomNavigationPadding(findifyDataContext);
    }, [findifyDataContext.visitWorkspaceSelected, findifyDataContext?.workspace?.app_settings?.enable_sidemenu]);

    useEffect(() => {
        const modules = findifyDataContext?.workspace?.app_settings?.app_menu_lines ?? [];
        setAvailableMenuItems(modules);

        findifyMenuContext.setIsSidemenu(!isInitialPage && findifyDataContext?.workspace?.app_settings?.enable_sidemenu);

        setAvailableModules(groupMenuItems(modules));
    }, [i18n.language]);

    useEffect(() => {
        let isMounted = true;
        if (!findifyDataContext?.visitWorkspaceSelected || !findifyDataContext?.workspace?.primary_domain?.domain_name) {
            setSideMenuItems([]);
            setSideMenuModules([]);
            findifyMenuContext.setHasSidemenuItems(false);
            return;
        }

        const menusApi = getApiFromModule('menus', findifyDataContext);
        menusApi?.getList(0, 100, null, {}, null)
            .then((res: any) => {
                const menu = (res?.results ?? []).find((line: any) => (
                    getMenuTitleValues(line).some((title: string) => title.trim().toLocaleLowerCase() === 'menu-app')
                    || String(line?.page_slug ?? line?.slug ?? '').trim().toLocaleLowerCase() === 'menu-app'
                ));
                if (menu?.items?.length) return { results: menu.items };
                if (!menu?.menu_id) return [];
                const menuItemsApi = getApiFromModule('menus_items', findifyDataContext, menu.menu_id);
                return menuItemsApi?.getList(0, 100, null, {}, null);
            })
            .then((res: any) => {
                if (!isMounted) return;
                const items = (Array.isArray(res) ? res : res?.results ?? [])
                    .map(normalizeMenuItem)
                    .filter((item: any) => item && (item.type || item.module || item.url || item.content_id || item.point_of_interest_id));
                setSideMenuItems(items);
                setSideMenuModules(groupMenuItems(items));
                findifyMenuContext.setHasSidemenuItems(items.length > 0);
            })
            .catch(() => {
                if (!isMounted) return;
                setSideMenuItems([]);
                setSideMenuModules([]);
                findifyMenuContext.setHasSidemenuItems(false);
            });

        return () => {
            isMounted = false;
        }
    }, [findifyDataContext?.visitWorkspaceSelected, findifyDataContext?.workspace?.workspace_id, findifyDataContext?.workspace?.primary_domain?.domain_name, i18n.language]);

    const hasBiscainhosOverflowMenu = !isInitialPage
        && Number(findifyDataContext?.workspace?.workspace_id) === 155
        && !findifyMenuContext.isSidemenu
        && (sideMenuItems.length > 0 || availableMenuItems.length > 4);

    const sidemenuModulesToRender = sideMenuModules.length > 0 ? sideMenuModules : availableModules;

    // sidemenu
    return <>
        {
            findifyMenuContext.isSidemenu &&
            <SideMenuNavigationElement isLogged={isLogged} navigation={navigation} findifyDataContext={findifyDataContext} currentPage={currentPage} availableModules={sidemenuModulesToRender} />
        }
        {
            hasBiscainhosOverflowMenu &&
            <SideMenuNavigationElement isLogged={isLogged} navigation={navigation} findifyDataContext={findifyDataContext} currentPage={currentPage} availableModules={sidemenuModulesToRender} />
        }
        {
            !findifyMenuContext.isSidemenu &&
            <BottomNavigationElement isLogged={isLogged} navigation={navigation} isInitialPage={isInitialPage} findifyDataContext={findifyDataContext} currentPage={currentPage} currentParams={currentParams} availableModules={availableModules} availableMenuItems={availableMenuItems} />
        }
        {/* ENABLE QA MODE */}
        <Pressable
            style={{
                position: 'absolute',
                bottom: insets.bottom,
                right: 0,
                // height: 40,
                padding: 10,
                backgroundColor: 'red',
                zIndex: (isInitialPage || IS_QA_MODE) ? 100 : -10,
                opacity: IS_QA_MODE ? 1 : 0,
            }}
            onPress={() => {
                // ONLY ENABLE OR DISABLE ON INITIAL PAGE
                if (isInitialPage) {
                    setQAPressCount(QAPressCount + 1);
                    // Set a timer to reset the press count 
                    if (!QAPressTimer) {

                        const pressTimer = setTimeout(() => {
                            setQAPressCount(0);
                            setQAPressTimer(null);
                        }, 6000);
                        setQAPressTimer(pressTimer);
                    }

                    return () => {
                        // Clear the timer if the component unmounts
                        if (QAPressTimer) {
                            clearTimeout(QAPressTimer);
                        }
                    };
                }
            }}
        >
            <View>
                <PlaceText>{IS_QA_MODE ? 'QA' : 'PROD'}</PlaceText>
            </View>
        </Pressable>
    </>

}


const makeSidemenuLine = (item: any, index: any, navigation: any, t: any, findifyDataContext: FindifyDataContextType, findifyMenuContext: FindifyMenuContextType, isLogged: boolean) => {
    return <Pressable key={'sidemenu_item_' + index} onPressIn={() => {
        let success = openMenuItem(item, navigation, t, findifyDataContext, isLogged);
        if (success) {
            findifyMenuContext.setIsSidemenuOpen(false);
        }
    }}>
        <View style={{
            display: 'flex',
            flexDirection: 'row',
            paddingVertical: 8,
            paddingHorizontal: 16,
            alignContent: 'center',
            alignItems: 'center',
            gap: 12,
            marginVertical: 2,
            marginHorizontal: 8,
            borderRadius: 12,
        }}>
            <Icon name={item?.icon} size={26} color={PlaceColors(findifyDataContext).sidebarTextColor.get()} />
            <PlaceText style={{ color: PlaceColors(findifyDataContext).sidebarTextColor.get(), fontSize: 16, fontWeight: '500' }}>{getTranslatedString(item.label, findifyDataContext)}</PlaceText>
        </View>
    </Pressable>
}

export const toggleSidemenu = (findifyMenuContext: FindifyMenuContextType) => findifyMenuContext.setIsSidemenuOpen(!findifyMenuContext.isSidemenuOpen);
const SideMenuNavigationElement = ({
    navigation,
    findifyDataContext,
    currentPage,
    availableModules,
    isLogged,
}: {
    navigation: any,
    findifyDataContext: any,
    currentPage: string,
    availableModules: any,
    isLogged: boolean
}) => {

    const findifyMenuContext = useContext(FindifyMenuContext);
    const insets = useSafeAreaInsets();
    const opacity = useRef(new Animated.Value(0)).current;
    const translateX = useRef(new Animated.Value(0)).current;
    const { t } = useTranslation();
    const [renderImageSize, setRenderImageSize] = useState<any>({});

    useEffect(() => {
        if (findifyDataContext.workspace?.app_settings?.app_logo_url) {
            Image.getSize(findifyDataContext.workspace?.app_settings?.app_logo_url, (w, h) => {
                const aspectRatio = w / h;

                const maxHeight = 100;
                const maxWidth = Dimensions.get('window').width * 0.60 * 0.9; // Sidebar is 60% of screen, logo up to 90% of sidebar

                let width = maxWidth;
                let height = maxHeight;

                if (aspectRatio > 1) {
                    // Landscape image
                    height = maxWidth / aspectRatio;
                } else if (aspectRatio < 1) {
                    // Portrait image
                    width = maxHeight * aspectRatio;
                }

                // Ensure the calculated dimensions are within the specified bounds
                if (width > maxWidth) {
                    width = maxWidth;
                    height = width / aspectRatio;
                }

                if (height > maxHeight) {
                    height = maxHeight;
                    width = height * aspectRatio;
                }

                setRenderImageSize({
                    width,
                    height,
                })
            });
        }
    }, [findifyDataContext.workspace?.app_settings?.app_logo_url]);

    useEffect(() => {
        Animated.timing(translateX, {
            toValue: (!findifyMenuContext.isSidemenuOpen ? 0 : (Dimensions.get('window').width * 2)),
            duration: 400, // Animation duration in milliseconds
            useNativeDriver: true, // You can set this to true if using some transforms for better performance
        }).start();
        Animated.timing(opacity, {
            toValue: (!findifyMenuContext.isSidemenuOpen ? 0 : 1),
            delay: !findifyMenuContext.isSidemenuOpen ? 0 : 200,
            duration: 200, // Animation duration in milliseconds
            useNativeDriver: true, // Using the native driver for performance
        }).start();
    }, [findifyMenuContext.isSidemenuOpen]);



    return <>
        <Animated.View
            style={{
                left: '-200%',
                position: 'absolute',
                top: 0,
                zIndex: 10,
                width: '200%',
                height: '100%',
                opacity,
                transform: [{ translateX }],
                backgroundColor: '#00000080',
            }}
        >

            <View
                style={{
                    backgroundColor: PlaceColors(findifyDataContext).sidebarBackgroundColor.get(),
                    zIndex: 10,
                    width: '30%', // 60 / 4 as container is 4x screen width
                    height: '100%',
                    paddingTop: insets.top,
                    paddingBottom: insets.bottom,
                    borderTopRightRadius: 32,
                    borderBottomRightRadius: 32,
                    ...DefaultShadow,
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                }}
            >
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    showsHorizontalScrollIndicator={false}
                    bounces={false}
                    contentContainerStyle={{
                        display: 'flex',
                        flexDirection: 'column',
                        marginLeft: 10,
                    }}
                >
                    <Pressable
                        onPress={() => toggleSidemenu(findifyMenuContext)}
                    >
                        {makeSidemenuLine({
                            label: '',
                            icon: 'close-outline',
                        }, -1, navigation, t, findifyDataContext, findifyMenuContext, isLogged)}
                    </Pressable>
                    {
                        findifyDataContext.workspace?.app_settings?.app_logo_url &&
                        <View style={{
                            paddingBottom: 10,
                            marginBottom: 10,
                            marginTop: -10, // Pull up closer to close button
                            borderBottomWidth: 1,
                            borderBottomColor: PlaceColors(findifyDataContext).sidebarTextColor.withAlpha(50),
                            alignItems: 'center',
                            width: '100%',
                            alignSelf: 'center'
                        }}>
                            <Image
                                style={{
                                    ...renderImageSize,
                                }}
                                resizeMode="contain"
                                source={{ uri: findifyDataContext.workspace?.app_settings?.app_logo_url }} />
                        </View>
                    }
                    {availableModules.map((row: any, rindex: number) => {
                        return <>
                            {row.map((item: any, index: number) => {
                                return makeSidemenuLine(item, `sidemenu-line-${rindex}-${index}`, navigation, t, findifyDataContext, findifyMenuContext, isLogged);
                            })}
                        </>
                    })}

                </ScrollView>
                <View
                    style={{
                        paddingHorizontal: 20,
                        paddingBottom: 10,
                        paddingTop: 5,
                        display: 'flex',
                        gap: 4
                    }}
                >
                    <BiscainhosFooterLogo findifyDataContext={findifyDataContext} variant="sidemenu" />
                    {findifyDataContext.workspace?.app_settings?.socials_lines.map((line: any, index: number) => {
                        return makeSocialLine(line?.icon, index, getTranslatedString(line?.label, findifyDataContext), findifyDataContext, () => openSocial(navigation, line, t));
                    })}
                    <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', gap: 4, borderTopColor: PlaceColors(findifyDataContext).sidebarTextColor.get(), borderTopWidth: 1, paddingTop: 5, marginTop: 2 }}>
                        {makeSocialLine('person-circle-outline', 45132, t('profile_tab.profile'), findifyDataContext, () => {
                            openMenuItem({
                                icon: 'person-circle-outline',
                                label: t('profile_tab.profile'),
                                type: 'profile',
                            }, navigation, t, findifyDataContext, isLogged);
                            findifyMenuContext.setIsSidemenuOpen(false);
                        })}
                        {!Config.WORKSPACE_ID &&  makeSocialLine('chevron-back-outline', 54323, t('profile_tab.change_place'), findifyDataContext, () => {
                            openMenuItem({
                                icon: 'chevron-back-outline',
                                label: t('profile_tab.change_place'),
                                type: 'change_current_workspace',
                            }, navigation, t, findifyDataContext, isLogged);
                            findifyMenuContext.setIsSidemenuOpen(false);
                        })}
                    </View>
                </View>
            </View>
        </Animated.View>
    </>

}

const makeSocialLine = (icon: any, index: number, label: string, findifyDataContext: FindifyDataContextType, action: Function) => {
    return <Pressable
        key={'social_line_key' + index}
        style={{
            display: 'flex',
            flexDirection: 'row',
            gap: 6,
            paddingVertical: 2
        }}
        onPress={() => action()}>
        <Icon style={{ color: PlaceColors(findifyDataContext).sidebarTextColor.get() }} name={icon} size={16} />
        <PlaceText
            style={{
                color: PlaceColors(findifyDataContext).sidebarTextColor.get(),
            }}
        >{label}</PlaceText>
    </Pressable>
}

const BottomNavigationElement = ({
    navigation,
    isInitialPage,
    findifyDataContext,
    currentPage,
    currentParams,
    availableModules,
    availableMenuItems,
    isLogged,
}: {
    navigation: any,
    isInitialPage: boolean,
    findifyDataContext: any,
    currentPage: string,
    currentParams: any,
    availableModules: any,
    availableMenuItems: any[],
    isLogged: boolean
}) => {
    const insets = useSafeAreaInsets();
    const [modalOpen, setModalOpen] = useState(false);
    const { t } = useTranslation();

    if (!isInitialPage && Number(findifyDataContext?.workspace?.workspace_id) === 155) {
        return <BiscainhosBottomNavigation
            navigation={navigation}
            findifyDataContext={findifyDataContext}
            currentPage={currentPage}
            currentParams={currentParams}
            availableMenuItems={availableMenuItems}
            isLogged={isLogged}
        />
    }

    return <>
        {!isInitialPage && <FindifyBasicModal
            // isOpen={true}
            transparent={true}
            isOpen={modalOpen}
            closeModal={() => setModalOpen(false)}
            align="flex-end"
        >
            <View style={[modalStyle.modalView, { marginBottom: insets.bottom + 85, backgroundColor: PlaceColors(findifyDataContext).primary.withAlpha(220) }]}>
                {availableModules.map((row: any, rindex: number) => {
                    return <View key={'bottom_nav_row_' + rindex} style={modalStyle.modalRow}>
                        {row.map((item: any, index: number) => {
                            return makeModalMenuItem(setModalOpen, item, `_${rindex}_${index}`, navigation, t, findifyDataContext, isLogged);
                        })}
                    </View>
                })}
            </View>
        </FindifyBasicModal>}

        <View style={[styles.conatiner, {
            marginBottom: insets.bottom,
            borderTopColor: PlaceColors(findifyDataContext).primary.get(),
            backgroundColor: PlaceColors(findifyDataContext).bottomBar.get(),
            height: 50,
        }]} >
            {isInitialPage ? <>
                {makeBottomTabIcon('Scan', 'qr-code-outline', navigation, currentPage, findifyDataContext, isInitialPage, false)}
            </> : <>
                {makeBottomTabIcon('Place', 'information-circle-outline', navigation, currentPage, findifyDataContext)}
                {<TouchableOpacity
                    style={[styles.button, {
                        backgroundColor: PlaceColors(findifyDataContext).findifyPrimary.get(),
                    }]}
                    onPress={() => { setModalOpen(true); }}
                >
                    <Svg width={50} height={50} color={'#FFFFFF'}>
                        <Path
                            fill="#FFFFFF"
                            d="M34.957 7.69c2.885 2.34 4.768 5.405 5.729 8.977.811 8.845-2.087 14.991-7.506 21.875-4.517 5.358-4.517 5.358-7.2 7.046-2.673-.037-4.147-1.762-5.999-3.5-3.76-3.894-7.139-8.122-9.197-13.166l-.469-1.005c-1.714-4.157-1.749-9.726-.235-13.946 2.315-4.776 5.65-7.089 10.508-9.069 5.036-1.552 10.115-.121 14.369 2.788Zm-14.369 8.486c-1.198 1.43-1.449 2.28-1.685 4.136.26 2.11.79 3.083 2.175 4.687 1.698 1.17 2.62 1.476 4.688 1.44 2.183-.596 3.453-1.478 4.779-3.309.717-2.281.607-3.963-.46-6.097-1.285-1.487-2.271-2.13-4.105-2.817-2.264 0-3.614.647-5.392 1.961Z"
                        />
                    </Svg>
                </TouchableOpacity>}
                {makeBottomTabIcon('Profile', 'person-circle-outline', navigation, currentPage, findifyDataContext)}
            </>
            }
        </View >
    </>
}
const modalStyle = StyleSheet.create({
    modalView: {

        width: '85%',
        display: 'flex',
        borderRadius: 32,
        paddingVertical: 20,
        // padding: 25,
        alignItems: 'center',
        shadowColor: '#000',
        shadowOffset: {
            width: 0,
            height: 10,
        },
        shadowOpacity: 0.3,
        shadowRadius: 15,
        elevation: 10,

    },
    modalRow: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-evenly',
        alignItems: 'center',
        width: '100%',
        marginVertical: 10,
        gap: 10,
    },
    item: {
        display: 'flex',
        width: 90,

        // height: '100%',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 12,
        borderRadius: 16,
        color: '#fff',
        textDecorationColor: '#fff',
    }
})

const styles = StyleSheet.create({
    //Modal
    //Base
    button: {
        top: -10,
        justifyContent: 'center',
        alignItems: 'center',
        width: 70,
        height: 70,
        margin: -15,
        borderRadius: 40,
        fontSize: 20,
    },
    buttonIcon: {
        color: '#F6F7EB'
    },
    conatiner: {
        paddingTop: 10,
        flexDirection: 'row',
        width: '100%',
        zIndex: 3,
        borderTopWidth: 3,
        justifyContent: 'space-around',
        position: 'absolute',
        bottom: 0,
    },
    biscainhosBottomContainer: {
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 3,
        minHeight: 76,
        paddingTop: 7,
        paddingHorizontal: 8,
        borderTopWidth: 1,
        flexDirection: 'row',
        alignItems: 'stretch',
        justifyContent: 'space-between',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: -2 },
        shadowOpacity: 0.08,
        shadowRadius: 10,
        elevation: 6,
    },
    biscainhosFooterLogoWrap: {
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    biscainhosFooterLogoWrapBottom: {
        minHeight: 50,
        marginTop: 28,
        paddingHorizontal: 22,
        paddingTop: 14,
        paddingBottom: 10,
        backgroundColor: 'rgba(250, 247, 241, 0.94)',
        borderTopWidth: 1,
        borderTopColor: 'rgba(153, 0, 0, 0.10)',
    },
    biscainhosFooterLogoWrapSidemenu: {
        paddingBottom: 12,
        marginBottom: 8,
        borderBottomWidth: 1,
        borderBottomColor: 'rgba(255,255,255,0.25)',
    },
    biscainhosFooterLogo: {
        width: '70%',
        maxWidth: 220,
        height: 34,
    },
    biscainhosFooterLogoSidemenu: {
        width: '88%',
        maxWidth: 180,
        height: 42,
    },
    biscainhosFooterLogoSecondary: {
        marginTop: 8,
    },
    biscainhosBottomItem: {
        flex: 1,
        minHeight: 58,
        marginHorizontal: 2,
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
        gap: 3,
        paddingHorizontal: 2,
        paddingVertical: 6,
        opacity: 0.72,
    },
    biscainhosBottomItemActive: {
        opacity: 1,
    },
    biscainhosBottomLabel: {
        textAlign: 'center',
        fontSize: 10,
        lineHeight: 13,
        fontWeight: '700',
    }
})
